import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Radio, { RadioComponentProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { GridContainerProps } from "@store/MobiusOverrides/GridContainer/GridContainer";
import { getFieldValue } from "@store/Store/Pages/CreateAccountForm/CreateAccountFormSelectors";
import setFieldValue from "@store/Store/Pages/CreateAccountForm/Handlers/SetFieldValue";
import useFieldValidation, {
    useConditionalFieldValidation,
} from "@store/Store/Pages/CreateAccountForm/Hooks/useFieldValidation";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { TYPOGRAPHY } from "@store/theme/design";
import { formGridProps } from "@store/theme/props/form";
import { emailsRegexp } from "@store/Widgets/CreateAccount/CreateAccountForm";
import React, { ChangeEvent, FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const fieldKey = "existingAccount";
const fieldKeyNumber = "existingAccountNumber";
const fieldKeyFullName = "existingAccountLoginAdminFullName";
const fieldKeyEmail = "existingAccountLoginAdminEmail";

const existingCustomerKey = "existing_customer";
const newCustomerKey = "new_customer";

const enum fields {
    label = "label",
    existingCustomerType = "existingCustomerType",
    newCustomerType = "newCustomerType",
    isRequired = "isRequired",

    requiredAccountNumber = "requiredAccountNumber",
    showAccountNumber = "showAccountNumber",
    labelAccountNumber = "labelAccountNumber",
    hintTextAccountNumber = "hintTextAccountNumber",
    requiredAccountFullName = "requiredAccountFullName",
    showAccountFullName = "showAccountFullName",
    labelAccountFullName = "labelAccountFullName",
    hintTextAccountFullName = "hintTextAccountFullName",
    requiredAccountEmail = "requiredAccountEmail",
    showAccountEmail = "showAccountEmail",
    labelAccountEmail = "labelAccountEmail",
    hintTextAccountEmail = "hintTextAccountEmail",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.newCustomerType]: string;
        [fields.existingCustomerType]: string;
        [fields.isRequired]: boolean;

        [fields.requiredAccountNumber]: boolean;
        [fields.showAccountNumber]: boolean;
        [fields.labelAccountNumber]: string;
        [fields.hintTextAccountNumber]: string;
        [fields.requiredAccountFullName]: boolean;
        [fields.showAccountFullName]: boolean;
        [fields.labelAccountFullName]: string;
        [fields.hintTextAccountFullName]: string;
        [fields.requiredAccountEmail]: boolean;
        [fields.showAccountEmail]: boolean;
        [fields.labelAccountEmail]: string;
        [fields.hintTextAccountEmail]: string;
    };
}

const mapStateToProps = (state: StoreApplicationState) => ({
    value: getFieldValue(state, fieldKey),
    valueAccountNumber: getFieldValue(state, fieldKeyNumber),
    valueAccountFullName: getFieldValue(state, fieldKeyFullName),
    valueAccountEmail: getFieldValue(state, fieldKeyEmail),
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CreateAccountAccountTypeStyles {
    typeRadioWrapper?: InjectableCss;
    typeRadio?: RadioGroupProps;
    typeValueRadio?: RadioComponentProps;
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    gridItemRadio?: GridItemProps;
    additionnalTextField?: TextFieldPresentationProps;
}

export const createAccountAccountTypeStyles: CreateAccountAccountTypeStyles = {
    typeRadioWrapper: {
        css: css`
            display: flex;
            flex-direction: row;

            ${RadioStyle} {
                margin-top: 0px !important;
                margin-right: 10px;
            }
        `,
    },
    typeRadio: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
        errorProps: {
            css: css`
                margin-top: 6px;
                line-height: ${TYPOGRAPHY.size.small} !important;
                font-size: ${TYPOGRAPHY.size.smallest} !important;
                font-weight: ${TYPOGRAPHY.weight.base} !important;
            `,
        },
    },
    container: formGridProps,
    gridItemRadio: {
        width: 12,
    },
    gridItem: {
        width: [12, 6, 6, 6, 6],
    },
    additionnalTextField: {
        cssOverrides: {
            formField: css``,
        },
    },
};

const styles = createAccountAccountTypeStyles;

const CreateAccountAccountType: FC<Props> = ({
    fields,
    value,
    valueAccountNumber,
    valueAccountFullName,
    valueAccountEmail,
    setFieldValue,
}) => {
    const {
        label,
        newCustomerType,
        existingCustomerType,
        isRequired,
        requiredAccountNumber,
        showAccountNumber,
        labelAccountNumber,
        hintTextAccountNumber,
        requiredAccountFullName,
        showAccountFullName,
        labelAccountFullName,
        hintTextAccountFullName,
        requiredAccountEmail,
        showAccountEmail,
        labelAccountEmail,
        hintTextAccountEmail,
    } = fields;
    const [errorMessage, setErrorMessage] = useState("");
    const [isExisting, setIsExisting] = useState(false);
    const [errorMessageAccountNumber, setErrorMessageAccountNumber] = useState("");
    const [errorMessageAccountFullName, setErrorMessageAccountFullName] = useState("");
    const [errorMessageAccountEmail, setErrorMessageAccountEmail] = useState("");

    const capitalizeFirstLetter = (str: string) => {
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };
    React.useEffect(() => {
        const _isExisting = value === existingCustomerKey;
        setIsExisting(_isExisting);
        if (!_isExisting) {
            setFieldValue({ key: fieldKeyNumber, value: "" });
            setFieldValue({ key: fieldKeyFullName, value: "" });
            setFieldValue({ key: fieldKeyEmail, value: "" });
        }
    }, [value]);

    const validate = () => {
        const message = isRequired && !value ? (siteMessage("Field_Required", label) as string) : "";
        setErrorMessage(message);
        return !message;
    };

    const validateAccountNumber = () => {
        const messageAccountNumber =
            requiredAccountNumber && !valueAccountNumber
                ? (siteMessage("Field_Required", capitalizeFirstLetter(labelAccountNumber)) as string)
                : "";
        setErrorMessageAccountNumber(messageAccountNumber);
        return !messageAccountNumber;
    };

    const validateAccountFullName = () => {
        const messageAccountFullName =
            requiredAccountFullName && !valueAccountFullName
                ? (siteMessage("Field_Required", capitalizeFirstLetter(labelAccountFullName)) as string)
                : "";
        setErrorMessageAccountFullName(messageAccountFullName);
        return !messageAccountFullName;
    };

    const validateAccountEmail = () => {
        const messageAccountEmail =
            requiredAccountEmail && !valueAccountEmail
                ? (siteMessage("Field_Required", capitalizeFirstLetter(labelAccountEmail)) as string)
                : !valueAccountEmail || emailsRegexp.test(valueAccountEmail)
                ? ""
                : (siteMessage("ContactUsForm_EmailIsInvalidErrorMessage") as string);
        setErrorMessageAccountEmail(messageAccountEmail);
        return !messageAccountEmail;
    };

    useConditionalFieldValidation(fieldKeyNumber, valueAccountNumber, isExisting, validateAccountNumber);
    useConditionalFieldValidation(fieldKeyFullName, valueAccountFullName, isExisting, validateAccountFullName);
    useConditionalFieldValidation(fieldKeyEmail, valueAccountEmail, isExisting, validateAccountEmail);
    useFieldValidation(fieldKey, value, validate);

    const changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: fieldKey, value: event.target.value });
    };
    const changeMessageAccountNumberHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: fieldKeyNumber, value: event.target.value });
    };
    const changeMessageAccountFullNameHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: fieldKeyFullName, value: event.target.value });
    };
    const changeMessageAccountEmailHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: fieldKeyEmail, value: event.target.value });
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.gridItemRadio}>
                <RadioGroup
                    {...styles.typeRadio}
                    label={label}
                    value={value}
                    error={errorMessage}
                    onChangeHandler={changeHandler}
                    required={isRequired}
                >
                    <StyledWrapper {...styles.typeRadioWrapper}>
                        <Radio {...styles.typeValueRadio} value={existingCustomerKey}>
                            {existingCustomerType}
                        </Radio>
                        <Radio {...styles.typeValueRadio} value={newCustomerKey}>
                            {newCustomerType}
                        </Radio>
                    </StyledWrapper>
                </RadioGroup>
            </GridItem>
            {isExisting && (showAccountNumber || showAccountFullName || showAccountEmail) && (
                <>
                    {isExisting && showAccountNumber && (
                        <GridItem {...styles.gridItem}>
                            <TextField
                                {...styles.additionnalTextField}
                                label={labelAccountNumber}
                                required={requiredAccountNumber}
                                placeholder={hintTextAccountNumber}
                                value={valueAccountNumber}
                                error={errorMessageAccountNumber}
                                onChange={changeMessageAccountNumberHandler}
                            />
                        </GridItem>
                    )}
                    {isExisting && showAccountFullName && (
                        <GridItem {...styles.gridItem}>
                            <TextField
                                {...styles.additionnalTextField}
                                label={labelAccountFullName}
                                required={requiredAccountFullName}
                                placeholder={hintTextAccountFullName}
                                value={valueAccountFullName}
                                error={errorMessageAccountFullName}
                                onChange={changeMessageAccountFullNameHandler}
                            />
                        </GridItem>
                    )}
                    {isExisting && showAccountEmail && (
                        <GridItem {...styles.gridItem}>
                            <TextField
                                {...styles.additionnalTextField}
                                label={labelAccountEmail}
                                required={requiredAccountEmail}
                                placeholder={hintTextAccountEmail}
                                value={valueAccountEmail}
                                error={errorMessageAccountEmail}
                                onChange={changeMessageAccountEmailHandler}
                            />
                        </GridItem>
                    )}
                </>
            )}
        </GridContainer>
    );
};

const getCmsFields = (
    field: string,
    required: string,
    show: string,
    label: string,
    hintText: string,
    labelDefaultValue: string,
    hintTextDefaultValue: string,
) => {
    return [
        {
            name: show,
            displayName: `Enable Field ${field}`,
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
        },
        {
            name: required,
            displayName: `Require ${field}`,
            editorTemplate: "CheckboxField",
            defaultValue: true,
            tooltip: "It will be required only if enabled and if user has an existing Elk Home account",
            fieldType: "General",
        },
        {
            name: label,
            displayName: `Label for ${field}`,
            editorTemplate: "TextField",
            fieldType: "Translatable",
            defaultValue: labelDefaultValue,
            isRequired: false,
        },
        {
            name: hintText,
            displayName: `Hint Text for ${field}`,
            editorTemplate: "TextField",
            fieldType: "Translatable",
            defaultValue: hintTextDefaultValue,
            isRequired: false,
        },
    ];
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CreateAccountAccountType),
    definition: {
        group: "Create Account" as any,
        displayName: "Existing Account",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Do you have an existing Elk Home account?",
                isRequired: true,
            },
            {
                name: fields.existingCustomerType,
                displayName: "Existing Customer Label (Yes)",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Yes",
                isRequired: true,
            },
            {
                name: fields.newCustomerType,
                displayName: "New Customer Label (No)",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "No",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            ...(getCmsFields(
                fieldKeyNumber,
                fields.requiredAccountNumber,
                fields.showAccountNumber,
                fields.labelAccountNumber,
                fields.hintTextAccountNumber,
                "Existing Account Number",
                "Enter your account number",
            ) as FieldDefinition[]),
            ...(getCmsFields(
                fieldKeyFullName,
                fields.requiredAccountFullName,
                fields.showAccountFullName,
                fields.labelAccountFullName,
                fields.hintTextAccountFullName,
                "Account Owner Full Name",
                "Enter the account owner's full name",
            ) as FieldDefinition[]),
            ...(getCmsFields(
                fieldKeyEmail,
                fields.requiredAccountEmail,
                fields.showAccountEmail,
                fields.labelAccountEmail,
                fields.hintTextAccountEmail,
                "Account Owner Email",
                "Enter the account owner's email",
            ) as FieldDefinition[]),
        ],
    },
};

export default widgetModule;
